import { useEffect, useState } from 'react';
import {
  Alert, Box, CircularProgress, IconButton, Link, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { CheckCircle, Cancel, ErrorOutline, HelpOutline, OpenInNew, Refresh } from '@mui/icons-material';
import {
  CTWA_CONFIG, CTWA_GET_AD_ACCOUNTS, CTWA_GET_FB_PAGES, CTWA_REFRESH_INSTAGRAM,
  CTWA_SAVE_AD_ACCOUNT, CTWA_SAVE_FB_PAGE, CTWA_WABA_PHONES,
} from '../../../config/urls';
import { ColorsConstants } from '../../../cssUtils/constants';
import { useCtwaApi } from './api';
import { FieldLabel, Note } from './ui';

const STATUS_ICON = {
  pass: <CheckCircle sx={{ fontSize: 18, color: '#166534' }} />,
  fail: <Cancel sx={{ fontSize: 18, color: '#B91C1C' }} />,
  warn: <ErrorOutline sx={{ fontSize: 18, color: '#B45309' }} />,
  unknown: <HelpOutline sx={{ fontSize: 18, color: ColorsConstants.primaryGray50 }} />,
};

const digits = (s) => (s || '').replace(/\D/g, '');

/**
 * Ad account, Page, WhatsApp number and (read-only) Instagram account, plus Meta pre-checks.
 * Each pick is saved to the tenant's CTWA config through the existing setup endpoints, so the
 * pre-checks (which read that config) always describe what is selected here.
 */
const AccountSetupSection = ({ account, onChange, config, onConfigRefresh, checks, checksLoading, onRecheck }) => {
  const api = useCtwaApi();
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [phones, setPhones] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState('');
  const [igRefreshing, setIgRefreshing] = useState(false);

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get(CTWA_GET_AD_ACCOUNTS), api.get(CTWA_GET_FB_PAGES), api.get(CTWA_WABA_PHONES),
      ]);
      const [acc, pg, ph] = results;
      if (acc.status === 'fulfilled') setAdAccounts(acc.value.ad_accounts || []);
      if (pg.status === 'fulfilled') setPages(pg.value.pages || []);
      if (ph.status === 'fulfilled') setPhones(ph.value.phone_numbers || []);
      const failed = results.filter((r) => r.status === 'rejected').map((r) => r.reason.message);
      if (failed.length) setLoadError(failed.join(' · '));
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (what, fn) => {
    setSaving(what);
    setLoadError('');
    try { await fn(); } catch (e) { setLoadError(e.message); } finally { setSaving(''); }
  };

  const pickAdAccount = (id) => save('account', async () => {
    const acc = adAccounts.find((a) => a.id === id);
    await api.post(CTWA_SAVE_AD_ACCOUNT, {
      ad_account_id: acc.id, name: acc.name, currency: acc.currency || 'INR', account_status: acc.account_status || 1,
    });
    onChange({ adAccountId: acc.id, adAccountName: acc.name });
  });

  const pickPage = (id) => save('page', async () => {
    const page = pages.find((p) => p.id === id);
    await api.post(CTWA_SAVE_FB_PAGE, {
      fb_page_id: page.id, fb_page_name: page.name, fb_page_category: page.category || '',
      picture_url: page.picture?.data?.url || '',
    });
    onChange({ pageId: page.id, pageName: page.name });
    await onConfigRefresh(); // the backend re-resolves the Page's Instagram account on save
  });

  const pickPhone = (id) => save('phone', async () => {
    const phone = phones.find((p) => p.id === id);
    const patch = { waNumberId: phone.id, waNumber: digits(phone.display_phone_number), waNumberName: phone.verified_name || '' };
    await api.post(CTWA_CONFIG, { wa_number_id: patch.waNumberId, wa_number: patch.waNumber, wa_number_name: patch.waNumberName });
    onChange(patch);
  });

  const refreshInstagram = async () => {
    setIgRefreshing(true);
    try {
      await api.post(CTWA_REFRESH_INSTAGRAM, {});
      await onConfigRefresh();
      onRecheck();
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setIgRefreshing(false);
    }
  };

  const select = (label, value, onPick, options, busyKey, hint) => (
    <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <TextField
        select fullWidth size="small" value={value || ''} disabled={!!saving}
        onChange={(e) => onPick(e.target.value)}
        InputProps={{ endAdornment: saving === busyKey ? <CircularProgress size={16} sx={{ mr: 3 }} /> : null }}
      >
        {options.length === 0 && <MenuItem value="" disabled>Nothing found</MenuItem>}
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    </Box>
  );

  const igConnected = !!config?.ig_connected;
  // The selected number may not be in the WABA list (e.g. set before), so match on digits.
  const selectedPhoneId = phones.find((p) => digits(p.display_phone_number) === digits(account.waNumber))?.id || account.waNumberId;

  return (
    <Box>
      <Note sx={{ mb: 2 }}>Pick where this campaign runs from. We check everything Meta needs before you continue.</Note>
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        {select('Ad account', account.adAccountId, pickAdAccount,
          adAccounts.map((a) => ({ value: a.id, label: `${a.name} · ${a.id}` })), 'account')}
        {select('Facebook Page', account.pageId, pickPage,
          pages.map((p) => ({ value: p.id, label: p.name })), 'page')}
        {select('WhatsApp number', selectedPhoneId, pickPhone,
          phones.map((p) => ({ value: p.id, label: `${p.display_phone_number} · ${p.verified_name || ''}` })), 'phone')}
        <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
          <FieldLabel hint="(optional)">Instagram account</FieldLabel>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TextField
              fullWidth size="small" disabled
              value={igConnected ? `@${config.ig_username}` : 'Not linked'}
              helperText="The Instagram account linked to the selected Page"
            />
            <Tooltip title="Check again for a linked Instagram account">
              <span>
                <IconButton aria-label="Refresh Instagram account" onClick={refreshInstagram} disabled={igRefreshing || !account.pageId}>
                  {igRefreshing ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </Stack>

      <Box sx={{ border: `1px solid ${ColorsConstants.primaryGray10}`, borderRadius: 2, p: 2, backgroundColor: '#F9FAFB' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={700}>Pre-checks</Typography>
          <IconButton size="small" aria-label="Run checks again" onClick={onRecheck} disabled={checksLoading}>
            {checksLoading ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
          </IconButton>
        </Stack>
        {!checks && !checksLoading && <Note>Pick an ad account to run the checks.</Note>}
        {(checks?.checks || []).map((c) => (
          <Stack key={c.key} direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.5 }}>
            {STATUS_ICON[c.status]}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">{c.label}</Typography>
              {c.status !== 'pass' && c.message && (
                <Typography variant="caption" color={c.status === 'fail' ? '#991B1B' : ColorsConstants.primaryGray50}>{c.message}</Typography>
              )}
            </Box>
            {c.status !== 'pass' && c.fix_url && (
              <Link href={c.fix_url} target="_blank" rel="noopener noreferrer" variant="caption" fontWeight={600} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                Fix <OpenInNew sx={{ fontSize: 12 }} />
              </Link>
            )}
          </Stack>
        ))}
        {checks && !checks.can_continue && (
          <Note sx={{ mt: 1, color: '#991B1B' }}>Fix the failed checks above, then run the checks again to continue.</Note>
        )}
      </Box>
    </Box>
  );
};

export default AccountSetupSection;
